import { useMemo } from 'react';

import { useWorkloads } from '@cast/features-cluster-shared';
import {
  OptimizationWorkload,
  WorkloadRecommendationStatusTypeEnum,
} from '@cast/types';

export type RecommendationStatusCardItem = {
  key: 'applied' | 'lowConfidence' | 'failed' | 'notAvailable';
  label: string;
  value: number;
};

type RecommendationStatusCardData = {
  total: number;
  appliedCount: number;
  appliedPercentage: number;
  items: RecommendationStatusCardItem[];
};

const getStatusCounts = (workloads: OptimizationWorkload[] | undefined) => {
  const initialCounts = {
    applied: 0,
    lowConfidence: 0,
    failed: 0,
    notAvailable: 0,
  };

  if (!workloads?.length) {
    return initialCounts;
  }

  return workloads.reduce((counts, workload) => {
    const { recommendationStatus } = workload;

    if (recommendationStatus.error) {
      counts.failed += 1;
    } else if (recommendationStatus.lowConfidence) {
      counts.lowConfidence += 1;
    } else if (
      recommendationStatus.type === WorkloadRecommendationStatusTypeEnum.APPLIED
    ) {
      counts.applied += 1;
    } else {
      counts.notAvailable += 1;
    }

    return counts;
  }, initialCounts);
};

export const useRecommendationStatusCardData = () => {
  const { workloads, isLoading, isFetching, error, refetch } = useWorkloads();

  const data: RecommendationStatusCardData | undefined = useMemo(() => {
    if (!workloads) {
      return undefined;
    }

    const counts = getStatusCounts(workloads);
    const total = workloads.length;
    const appliedPercentage =
      total > 0 ? Math.round((counts.applied / total) * 100) : 0;

    return {
      total,
      appliedCount: counts.applied,
      appliedPercentage,
      items: [
        {
          key: 'applied',
          label: 'Applied',
          value: counts.applied,
        },
        {
          key: 'lowConfidence',
          label: 'Low confidence',
          value: counts.lowConfidence,
        },
        {
          key: 'failed',
          label: 'Failed',
          value: counts.failed,
        },
        {
          key: 'notAvailable',
          label: 'N/A',
          value: counts.notAvailable,
        },
      ],
    };
  }, [workloads]);

  return {
    data,
    isLoading,
    isFetching,
    hasError: !!error,
    refetch,
  };
};
